'use client';

import { useState, useEffect } from 'react';
import {
  getCookiePreferences,
  setCookiePreferences,
  CookiePreferences,
  ACCEPT_ALL_PREFERENCES,
  REJECT_NON_ESSENTIAL_PREFERENCES,
  DEFAULT_PREFERENCES,
} from '@/lib/cookies';

interface CookieConsentProps {
  onPreferencesChange?: (preferences: CookiePreferences) => void;
}

export default function CookieConsent({ onPreferencesChange }: CookieConsentProps) {
  const [showModal, setShowModal] = useState(false);
  const [, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    // Check if user has already given consent
    const existingPreferences = getCookiePreferences();
    
    if (!existingPreferences) {
      // First time visit - show modal
      setShowModal(true);
    } else {
      // Returning visit - apply stored preferences
      setPreferences(existingPreferences);
      onPreferencesChange?.(existingPreferences);
    }
  }, [onPreferencesChange]);

  const handleAcceptAll = () => {
    const newPreferences = ACCEPT_ALL_PREFERENCES;
    setCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setShowModal(false);
    onPreferencesChange?.(newPreferences);
  };

  const handleRejectNonEssential = () => {
    const newPreferences = REJECT_NON_ESSENTIAL_PREFERENCES;
    setCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setShowModal(false);
    onPreferencesChange?.(newPreferences);
  };

  // Don't render on server-side to avoid hydration issues
  if (!mounted || !showModal) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-container">
        {/* Header */}
        <div className="cookie-consent-header">
          <div className="cookie-consent-title-container">
            <h3 className="cookie-consent-title">Cookies on desilo.studio</h3>
          </div>
        </div>

        {/* Content */}
        <div className="cookie-consent-content">
          <div className="cookie-consent-description-container">
            <p className="cookie-consent-description">
              We use cookies to enhance your browsing experience, serve personalized content, 
              and analyze our traffic. By clicking &quot;Accept&quot;, you consent to our use of cookies.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="cookie-consent-buttons">
          <button
            onClick={handleRejectNonEssential}
            className="cookie-consent-button cookie-consent-button-reject"
          >
            Reject non-essential
          </button>
          
          <button
            onClick={handleAcceptAll}
            className="cookie-consent-button cookie-consent-button-accept"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}